import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { TextField, ButtonMenu, Button } from '@ann/web-components';
import { useTemplateFilters } from '../../hooks/useTemplateFilters';
import { useDashboardData } from '../../context';
import { TEMPLATE_LIST_COPY } from '../../constants/dashboardBuilderCopy';
import { flattenSectionsForFilter } from '../../utils/dashboardFormatters';
import { FiltersRow, SearchWrapper, ClearButtonWrapper } from './DashboardFilters.styled';

const TemplateFilters = () => {
  const {
    localSearch,
    setLocalSearch,
    templateFilters,
    handleSectionChange,
    handleCategoryChange,
    resetFilters,
    hasActiveFilters,
  } = useTemplateFilters();

  const { sections, templateCategories, fetchSections, fetchTemplateCategories } =
    useDashboardData();
  const [filtersKey, setFiltersKey] = useState(0);

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  useEffect(() => {
    fetchTemplateCategories();
  }, [fetchTemplateCategories]);

  const flatSections = useMemo(
    () => flattenSectionsForFilter(sections),
    [sections]
  );
  const { filters: filtersCopy } = TEMPLATE_LIST_COPY;

  const sectionLabel = useMemo(() => {
    if (!templateFilters.sectionId) return filtersCopy.sectionPlaceholder;
    const section = flatSections.find((s) => s.id === templateFilters.sectionId);
    return section?.path || section?.name || filtersCopy.sectionPlaceholder;
  }, [templateFilters.sectionId, flatSections, filtersCopy.sectionPlaceholder]);

  const categoryLabel = useMemo(() => {
    if (!templateFilters.categoryId) return filtersCopy.categoryPlaceholder;
    const category = templateCategories.find(
      (c) => c.id === templateFilters.categoryId
    );
    return category?.name || filtersCopy.categoryPlaceholder;
  }, [
    templateFilters.categoryId,
    templateCategories,
    filtersCopy.categoryPlaceholder,
  ]);

  const handleSearchChange = useCallback((value) => {
    setLocalSearch(value ?? '');
  }, []);

  const handleReset = useCallback(() => {
    resetFilters();
    setFiltersKey((prev) => prev + 1);
  }, [resetFilters]);

  return (
    <FiltersRow>
      <SearchWrapper>
        <TextField
          iconLeft="search"
          size="small"
          placeholder={filtersCopy.searchPlaceholder}
          value={localSearch}
          onChange={handleSearchChange}
        />
      </SearchWrapper>

      <ButtonMenu
        key={`category-filter-${filtersKey}`}
        iconPosition="right"
        label={categoryLabel}
        menuPosition="bottom"
        onChange={handleCategoryChange}
        size="medium"
        type="secondary"
        width="auto"
        usePortal={false}
      >
        <ButtonMenu.Menu>
          {templateCategories.map((category) => (
            <ButtonMenu.Item
              key={category.id}
              label={category.name}
              value={category.id}
            >
              {category.name}
            </ButtonMenu.Item>
          ))}
        </ButtonMenu.Menu>
      </ButtonMenu>

      <ButtonMenu
        key={`section-filter-${filtersKey}`}
        iconPosition="right"
        label={sectionLabel}
        menuPosition="bottom"
        onChange={handleSectionChange}
        size="medium"
        type="secondary"
        width="auto"
        usePortal={false}
      >
        <ButtonMenu.Menu>
          {flatSections.map((section) => (
            <ButtonMenu.Item
              key={section.id}
              label={section.path || section.name}
              value={section.id}
            >
              {section.path || section.name}
            </ButtonMenu.Item>
          ))}
        </ButtonMenu.Menu>
      </ButtonMenu>

      {hasActiveFilters && (
        <ClearButtonWrapper>
          <Button
            label={filtersCopy.clearButton}
            size="medium"
            type="secondary"
            onClick={handleReset}
          />
        </ClearButtonWrapper>
      )}
    </FiltersRow>
  );
};

export default TemplateFilters;
